import React, { useState } from "react";
import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import { AddOutlined, Cancel } from "@mui/icons-material";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";

/**
 * Draws the control the backend registry names in `input`, not one guessed from `type`.
 * Adding an input type is a case here plus a registry entry — no other frontend change.
 */

// A control wider than this stops being readable - a settings card spans the page, the
// input inside it does not.
const FIELD_WIDTH = 520;

const Label = ({ children }) => {
    const colors = tokens(useTheme().palette.mode);
    return (
        <Typography fontWeight="bold" color={colors.grey[100]} fontSize={14} mb={0.75}>
            {children}
        </Typography>
    );
};

const Help = ({ error, children }) => {
    const colors = tokens(useTheme().palette.mode);
    return (
        <Typography
            variant="body2"
            fontSize={12.5}
            mt={0.75}
            sx={{ maxWidth: "60ch" }}
            color={error ? colors.redAccent[400] : colors.grey[300]}
        >
            {error || children}
        </Typography>
    );
};

const StringList = ({ config, value, error, onChange }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [draft, setDraft] = useState("");
    // Refusals that happen while typing, which the parent's save-time validation never sees.
    const [draftError, setDraftError] = useState("");

    // Stored as a JSON string so the backend can cast it; edited as a list.
    let items = [];
    try {
        const parsed = JSON.parse(value || "[]");
        items = Array.isArray(parsed) ? parsed : [];
    } catch {
        items = [];
    }

    const commit = (next) => onChange(JSON.stringify(next));

    // "email address" / "phone number", from the registry — so this control names what it
    // is collecting without knowing which field it is.
    const noun = config.item_label || "entry";

    const add = () => {
        const entry = draft.trim();
        if (!entry) return;
        // Caught here rather than on save: the duplicate is obvious while it is in hand.
        // The text is kept so it can be corrected instead of retyped.
        if (items.some((item) => item.toLowerCase() === entry.toLowerCase())) {
            setDraftError(`That ${noun} is already in the list`);
            return;
        }
        setDraftError("");
        commit([...items, entry]);
        setDraft("");
    };

    return (
        <Box>
            <Label>{config.label}</Label>
            <Box display="flex" gap={0.75} flexWrap="wrap" mb={items.length ? 1.25 : 0}>
                {items.map((item, index) => (
                    <Chip
                        key={`${item}-${index}`}
                        label={item}
                        onDelete={() => commit(items.filter((_, i) => i !== index))}
                        deleteIcon={
                            // The default delete icon has no name of its own, so a screen
                            // reader would announce four identical "remove" buttons.
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Remove ${item}`}
                                style={{ display: "inline-flex", alignItems: "center" }}
                            >
                                <Cancel sx={{ fontSize: 18 }} />
                            </span>
                        }
                        sx={{
                            backgroundColor: colors.primary[500],
                            color: colors.grey[100],
                            fontSize: 13,
                            "& .MuiChip-deleteIcon": { color: colors.grey[400] },
                            "& .MuiChip-deleteIcon:hover": { color: colors.redAccent[400] },
                        }}
                    />
                ))}
            </Box>
            <Box display="flex" gap={1} sx={{ maxWidth: FIELD_WIDTH }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="filled"
                    label={`Add ${/^[aeiou]/i.test(noun) ? "an" : "a"} ${noun}`}
                    value={draft}
                    error={Boolean(draftError)}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        if (draftError) setDraftError("");
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder={config.placeholder}
                />
                <Button
                    onClick={add}
                    disabled={!draft.trim()}
                    aria-label={`Add this ${noun}`}
                    sx={{ minWidth: 44, color: colors.grey[100], border: `1px solid ${colors.grey[700]}` }}
                >
                    <AddOutlined fontSize="small" />
                </Button>
            </Box>
            <Help error={draftError || error}>{config.help}</Help>
        </Box>
    );
};

const TextLike = ({ config, value, error, onChange, footnote }) => (
    <Box>
        <Label>{config.label}</Label>
        <TextField
            fullWidth
            sx={{ maxWidth: FIELD_WIDTH }}
            size="small"
            variant="filled"
            hiddenLabel
            type={config.input === "email" ? "email" : "text"}
            value={value}
            placeholder={config.placeholder}
            onChange={(e) => onChange(e.target.value)}
            error={Boolean(error)}
            inputProps={{ "aria-label": config.label }}
        />
        <Help error={error}>{footnote || config.help}</Help>
    </Box>
);

const ConfigField = (props) => {
    const { config } = props;

    if (config.input === "string_list") return <StringList {...props} />;
    return <TextLike {...props} />;
};

export default ConfigField;
